/**
 * @file Prevents the harmless "ResizeObserver loop completed with
 * undelivered notifications" (and the older "ResizeObserver loop limit
 * exceeded") browser warning from surfacing as an "Uncaught runtime error"
 * in the Create React App dev overlay.
 *
 * Why this is needed:
 *   Chromium and WebKit dispatch this notification when a `ResizeObserver`
 *   callback performs DOM work that triggers additional resize events
 *   within the same frame. The browser simply defers the extras to the
 *   next frame and nothing is lost — it is informational, not an error.
 *   However, `react-error-overlay` (shipped with `react-scripts@5`) listens
 *   to every `error` event on `window` and renders a full-screen red
 *   overlay that hides the app in development. Because the overlay's
 *   listener is registered from webpack-injected runtime code that runs
 *   before any app code, we cannot pre-empt it with `stopImmediatePropagation`
 *   (error events dispatched at `window` fire all listeners at AT_TARGET in
 *   registration order, regardless of capture flag). The reliable fix is
 *   therefore to prevent the loop condition from ever being reported in
 *   the first place.
 *
 * Primary fix — wrap `ResizeObserver` with a rAF-debounced subclass:
 *   Each callback invocation is deferred to the next animation frame so
 *   any layout work it triggers completes in its own frame, giving the
 *   browser room to deliver every queued notification without overflow.
 *   Deferring by one frame is within the ResizeObserver spec's scheduling
 *   guarantees and matches the pattern used by MUI, AG Grid, and many
 *   production apps. Entries from every native invocation inside a single
 *   frame are accumulated into one batch and delivered together on the
 *   next animation frame so consumers never lose an entry — naive
 *   rAF-debouncing (cancel + reschedule) would silently drop all but the
 *   latest batch when the native observer fires more than once per frame.
 *
 * Defensive fallback — swallow the specific window-level error:
 *   If a third-party library captured `window.ResizeObserver` at import
 *   time before this module evaluated (unlikely here given this module is
 *   imported first in `index.tsx`), the wrap would be bypassed. In that
 *   case we still try to quiet the message at the `window` level as a
 *   belt-and-suspenders measure.
 */

const RESIZE_OBSERVER_LOOP_MESSAGES = [
  "ResizeObserver loop completed with undelivered notifications.",
  "ResizeObserver loop limit exceeded",
];

const isResizeObserverLoopMessage = (message: unknown): boolean =>
  typeof message === "string" &&
  RESIZE_OBSERVER_LOOP_MESSAGES.some((entry) => message.includes(entry));

if (typeof window !== "undefined") {
  const NativeResizeObserver = window.ResizeObserver;
  if (NativeResizeObserver) {
    class DebouncedResizeObserver extends NativeResizeObserver {
      constructor(callback: ResizeObserverCallback) {
        let rafId = 0;
        // Accumulate entries from every native invocation in the current
        // frame so that subsequent `super(...)` calls within the same
        // frame extend the pending batch rather than replacing it.
        let pending: ResizeObserverEntry[] = [];
        super((entries, observer) => {
          pending.push(...entries);
          if (rafId) {
            return;
          }
          rafId = window.requestAnimationFrame(() => {
            const batch = pending;
            pending = [];
            rafId = 0;
            callback(batch, observer);
          });
        });
      }
    }
    window.ResizeObserver =
      DebouncedResizeObserver as unknown as typeof ResizeObserver;
  }

  window.addEventListener(
    "error",
    (event: ErrorEvent) => {
      if (
        isResizeObserverLoopMessage(event.message) ||
        isResizeObserverLoopMessage(
          (event.error as Error | undefined)?.message,
        )
      ) {
        event.stopImmediatePropagation();
        event.preventDefault();
      }
    },
    true,
  );
}

export {};
